import { Section } from "../components/Section";
import { FilmLabDiagram } from "../components/diagram/FilmLabDiagram";
import { LabStats } from "../components/LabStats";
import { ACTOR_COLOR } from "../components/diagram/diagramData";

const SURFACE = [
  { n: "24", label: "external API" },
  { n: "98", label: "internal API" },
  { n: "88", label: "MCP tools" },
  { n: "2", label: "autonomous agents" },
];

const LEGEND: { color: string; label: string }[] = [
  { color: ACTOR_COLOR.customer, label: "customer" },
  { color: ACTOR_COLOR.lab, label: "the lab / Holly" },
  { color: ACTOR_COLOR.both, label: "customer + lab" },
];

export function FilmLab() {
  return (
    <Section id="lab" label="the film lab" title="the film lab">
      <p className="mt-5 text-text-dim">
        In 2023, after winding down my wife and I's 8+ year wedding photography business, we had the
        wild idea to start a film development lab in our house. We both enjoyed shooting film, Holly
        learned to develop while getting her BFA, and we saw a gap in the market — in overall
        sentiment, accessibility, and price. After doing a little prospective math, the margin
        looked good, so we went for it.
      </p>

      {/* Live hero counters — right after the opening paragraph */}
      <div className="mt-8">
        <LabStats />
      </div>

      <div className="mt-8 space-y-4 text-text-dim">
        <p>
          We bought minilab equipment made in the mid-1990s on eBay (i.e., what drugstores and
          one-hour photo labs use), built a website, and put a dropbox on a concrete slab right
          outside our house. The first week, four people dropped off their film to be processed.
          About three years later, Holly processes 300–350 rolls of film per week, entirely by
          herself, and we've had people mail in film from over 150 Texas towns and 30 different
          states. I maintain that we are the most efficient per-person film lab <em>in the world</em>.
        </p>
        <p>
          We reached that self-proclaimed milestone with a fanatical commitment to automation. In
          the past three years, I have built hundreds of internal and external tools to support the
          needs of a business which are almost completely novel. Most film development labs are not
          contactless. Most do not exist in a home (we've since built a backhouse to accommodate the
          growth), and most processing and delivery are not handled by just one person. In order to
          do this, everything must run flawlessly all of the time.
        </p>
        <p>
          We started with a whole host of SaaS vendors — Stripe, Zapier, Retool, Google, Cloudflare,
          Mailchimp, SendGrid, and so on. Few fit our high-volume and/or user-friendly requirements.
          Over time, we've slowly whittled down to just Stripe, Google, and Cloudflare. Every
          surface a customer touches is now a completely in-house, purpose-built application. Every
          tool Holly uses to process orders is the same.
        </p>
        <p>
          In 2026 we built out a monthly subscription service with a core gallery application and
          have grown to 200+ paid monthly active users, and won{" "}
          <a
            href="https://calendar.austinchronicle.com/best-of/2026/services-critics-picks/best-analog-subscription-14117787"
            target="_blank"
            rel="noopener"
          >
            Best Analog Subscription
          </a>{" "}
          from the Austin Chronicle's Best of Austin series while continuing to drive our growth.
        </p>
        <p>
          The current production infrastructure of the lab is made up of <strong className="text-text">24</strong>{" "}
          external API endpoints, <strong className="text-text">98</strong> internal endpoints, and{" "}
          <strong className="text-text">88</strong> MCP tool calls. Almost 100% of this is run
          entirely serverless with Cloudflare Workers, D1, R2, KV, Queues, Email Routing, Email
          Sending, Workflows, and other functionality I'm probably forgetting.
        </p>
      </div>

      {/* Surface stat strip */}
      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {SURFACE.map((s) => (
          <div key={s.label} className="screen flex flex-col items-start gap-2 p-3.5">
            <span className="text-glow font-dseg text-[28px] leading-none tabular-nums text-amber">
              {s.n}
            </span>
            <span className="vfd-box">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Lab workflow: legend + how-to + the interactive loop, in one container */}
      <div className="panel relative mt-10 p-4 pt-6 md:p-6 md:pt-7">
        <span className="etch-label absolute -top-3 left-5 bg-bg px-2.5 !text-[13px] !text-amber">
          Lab workflow
        </span>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {LEGEND.map((l) => (
            <span key={l.label} className="flex items-center gap-2 font-mono text-[13px] text-text-dim">
              <i className="h-2.5 w-2.5 rounded-sm" style={{ background: l.color }} />
              {l.label}
            </span>
          ))}
        </div>
        <p className="mt-3 text-[14px] text-text-mute">
          Click on the apps in the boxes to see a sandboxed version of what our customers see
          (public apps) and what we see (operational apps).
        </p>
        <div className="mt-5">
          <FilmLabDiagram />
        </div>
      </div>
    </Section>
  );
}
